import {
  ACTIVATION_CODEC_FORMAT,
  activationEncodedByteLength,
  decodeActivationDelta,
  encodeActivationDelta,
  type EncodedActivation,
} from "./activation-codec";

export const NEXUS_LAYER_STREAM_TECH_ID = "NEXUS-LayerStream-v1" as const;
export const NEXUS_ACTIVATION_PACKET_FORMAT = "nexus-activation-packet-v1" as const;
export const NEXUS_LAYER_BYTE_PACKET_FORMAT = "nexus-layer-byte-packet-v1" as const;

export interface LayerStreamDevice {
  deviceId: string;
  startLayer: number;
  endLayerExclusive: number;
}

export interface LayerStreamChunk {
  chunkId: string;
  deviceId: string;
  startLayer: number;
  endLayerExclusive: number;
  layerCount: number;
  dependsOnChunkId: string | null;
  delivery: "on-demand-encrypted-stream";
}

export interface LayerStreamManifest {
  technologyId: typeof NEXUS_LAYER_STREAM_TECH_ID;
  modelId: string;
  totalLayers: number;
  chunks: readonly LayerStreamChunk[];
  modelDistribution: "layer-range-only";
  activationTransport: typeof NEXUS_ACTIVATION_PACKET_FORMAT;
  fullModelDistributed: false;
}

export interface ActivationPacket {
  format: typeof NEXUS_ACTIVATION_PACKET_FORMAT;
  codec: typeof ACTIVATION_CODEC_FORMAT;
  streamId: string;
  tokenIndex: number;
  layerStart: number;
  layerEndExclusive: number;
  sequence: number;
  totalSequences: number;
  encoded: EncodedActivation;
  originalBytes: number;
  encodedBytes: number;
}

export interface SerializedEncodedActivation {
  format: EncodedActivation["format"];
  mode: EncodedActivation["mode"];
  elementCount: number;
  blockSize: number;
  scalesBase64: string;
  payloadBase64: string;
}

export interface SerializedActivationPacket {
  format: typeof NEXUS_ACTIVATION_PACKET_FORMAT;
  codec: typeof ACTIVATION_CODEC_FORMAT;
  streamId: string;
  tokenIndex: number;
  layerStart: number;
  layerEndExclusive: number;
  sequence: number;
  totalSequences: number;
  encoded: SerializedEncodedActivation;
  originalBytes: number;
  encodedBytes: number;
}

export interface LayerBytePacket {
  format: typeof NEXUS_LAYER_BYTE_PACKET_FORMAT;
  streamId: string;
  modelId: string;
  chunkId: string;
  sequence: number;
  totalSequences: number;
  byteOffset: number;
  totalBytes: number;
  dataBase64: string;
}

export interface ActivationPacketMetrics {
  packetCount: number;
  originalBytes: number;
  encodedBytes: number;
  compressionRatio: number;
  spaceSavingFraction: number;
}

export interface AdaptiveActivationPacketization {
  packets: readonly ActivationPacket[];
  metrics: ActivationPacketMetrics;
  selectedBlockSize: number;
  maxAbsoluteError: number;
  strategy: "smallest-payload-within-error" | "fallback-lowest-error";
}

export interface LayerStreamLimits {
  maxLayers?: number;
  maxChunks?: number;
  maxActivationElements?: number;
  maxPacketPayloadBytes?: number;
}

const DEFAULT_LIMITS = {
  maxLayers: 4_096,
  maxChunks: 256,
  maxActivationElements: 16_777_216,
  maxPacketPayloadBytes: 2_000_000,
} as const;

function bytesToBase64(bytes: Uint8Array): string {
  const bufferCtor = globalThis as unknown as { Buffer?: { from(data: Uint8Array | string, encoding?: string): { toString(encoding: string): string } } };
  if (bufferCtor.Buffer) return bufferCtor.Buffer.from(bytes).toString("base64");
  let binary = "";
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return btoa(binary);
}

function base64ToBytes(value: string): Uint8Array {
  if (!/^[A-Za-z0-9+/]*={0,2}$/u.test(value) || value.length % 4 !== 0) {
    throw new Error("Invalid base64 payload");
  }
  const bufferCtor = globalThis as unknown as { Buffer?: { from(data: string, encoding: string): Uint8Array } };
  if (bufferCtor.Buffer) return new Uint8Array(bufferCtor.Buffer.from(value, "base64"));
  const binary = atob(value);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
  return bytes;
}

function float32ToBytes(values: Float32Array): Uint8Array {
  return new Uint8Array(values.buffer, values.byteOffset, values.byteLength);
}

function bytesToFloat32(bytes: Uint8Array): Float32Array {
  if (bytes.byteLength % Float32Array.BYTES_PER_ELEMENT !== 0) {
    throw new Error("Float32 byte payload is not aligned");
  }
  const copy = new Uint8Array(bytes);
  return new Float32Array(copy.buffer);
}

function limitValue(limits: LayerStreamLimits, key: keyof typeof DEFAULT_LIMITS): number {
  const value = limits[key] ?? DEFAULT_LIMITS[key];
  if (!Number.isSafeInteger(value) || value <= 0) throw new RangeError(`${key} must be a positive safe integer`);
  return value;
}

function assertLayerBounds(totalLayers: number, limits: LayerStreamLimits): void {
  const maxLayers = limitValue(limits, "maxLayers");
  if (!Number.isSafeInteger(totalLayers) || totalLayers <= 0 || totalLayers > maxLayers) {
    throw new RangeError("totalLayers is outside the LayerStream safety limit");
  }
}

function assertSafeId(value: string, label: string): void {
  if (!/^[A-Za-z0-9_.:-]{1,128}$/u.test(value)) {
    throw new Error(`${label} must be 1-128 safe identifier characters`);
  }
}

export function createLayerStreamManifest(
  modelId: string,
  totalLayers: number,
  devices: readonly LayerStreamDevice[],
  limits: LayerStreamLimits = {},
): LayerStreamManifest {
  assertSafeId(modelId, "modelId");
  assertLayerBounds(totalLayers, limits);
  const maxChunks = limitValue(limits, "maxChunks");
  if (devices.length === 0 || devices.length > maxChunks) throw new RangeError("device chunk count is outside the safety limit");

  let cursor = 0;
  const chunks = devices.map<LayerStreamChunk>((device, index) => {
    assertSafeId(device.deviceId, "deviceId");
    if (
      !Number.isSafeInteger(device.startLayer) ||
      !Number.isSafeInteger(device.endLayerExclusive) ||
      device.startLayer !== cursor ||
      device.endLayerExclusive <= device.startLayer ||
      device.endLayerExclusive > totalLayers
    ) {
      throw new RangeError("LayerStream chunks must be contiguous, ordered, non-empty, and inside totalLayers");
    }
    cursor = device.endLayerExclusive;
    return {
      chunkId: `${modelId}:layers:${device.startLayer}-${device.endLayerExclusive}`,
      deviceId: device.deviceId,
      startLayer: device.startLayer,
      endLayerExclusive: device.endLayerExclusive,
      layerCount: device.endLayerExclusive - device.startLayer,
      dependsOnChunkId: index === 0 ? null : `${modelId}:layers:${devices[index - 1].startLayer}-${devices[index - 1].endLayerExclusive}`,
      delivery: "on-demand-encrypted-stream",
    };
  });
  if (cursor !== totalLayers) throw new RangeError("LayerStream chunks must cover every layer exactly once");

  return {
    technologyId: NEXUS_LAYER_STREAM_TECH_ID,
    modelId,
    totalLayers,
    chunks,
    modelDistribution: "layer-range-only",
    activationTransport: NEXUS_ACTIVATION_PACKET_FORMAT,
    fullModelDistributed: false,
  };
}

export function packetizeActivation(
  input: {
    streamId: string;
    tokenIndex: number;
    layerStart: number;
    layerEndExclusive: number;
    activation: Float32Array;
    previousActivation?: Float32Array;
    maxPayloadBytes?: number;
    blockSize?: number;
  },
): readonly ActivationPacket[] {
  assertSafeId(input.streamId, "streamId");
  if (!Number.isSafeInteger(input.tokenIndex) || input.tokenIndex < 0) throw new RangeError("tokenIndex must be non-negative");
  if (
    !Number.isSafeInteger(input.layerStart) ||
    !Number.isSafeInteger(input.layerEndExclusive) ||
    input.layerEndExclusive <= input.layerStart
  ) {
    throw new RangeError("layer range is invalid");
  }
  const maxPayloadBytes = input.maxPayloadBytes ?? DEFAULT_LIMITS.maxPacketPayloadBytes;
  if (!Number.isSafeInteger(maxPayloadBytes) || maxPayloadBytes <= 0) {
    throw new RangeError("maxPayloadBytes must be a positive safe integer");
  }

  const encoded = encodeActivationDelta(input.activation, input.previousActivation, input.blockSize);
  const encodedBytes = activationEncodedByteLength(encoded);
  if (encodedBytes <= maxPayloadBytes) {
    return [
      {
        format: NEXUS_ACTIVATION_PACKET_FORMAT,
        codec: encoded.format,
        streamId: input.streamId,
        tokenIndex: input.tokenIndex,
        layerStart: input.layerStart,
        layerEndExclusive: input.layerEndExclusive,
        sequence: 0,
        totalSequences: 1,
        encoded,
        originalBytes: input.activation.byteLength,
        encodedBytes,
      },
    ];
  }

  const sliceCount = Math.ceil(encodedBytes / maxPayloadBytes);
  const elementsPerSlice = Math.ceil(input.activation.length / sliceCount);
  const packets: ActivationPacket[] = [];
  for (let sequence = 0; sequence < sliceCount; sequence += 1) {
    const start = sequence * elementsPerSlice;
    const end = Math.min(start + elementsPerSlice, input.activation.length);
    const previous = input.previousActivation?.slice(start, end);
    const sliceEncoded = encodeActivationDelta(input.activation.slice(start, end), previous, input.blockSize);
    packets.push({
      format: NEXUS_ACTIVATION_PACKET_FORMAT,
      codec: sliceEncoded.format,
      streamId: input.streamId,
      tokenIndex: input.tokenIndex,
      layerStart: input.layerStart,
      layerEndExclusive: input.layerEndExclusive,
      sequence,
      totalSequences: sliceCount,
      encoded: sliceEncoded,
      originalBytes: (end - start) * Float32Array.BYTES_PER_ELEMENT,
      encodedBytes: activationEncodedByteLength(sliceEncoded),
    });
  }
  return packets;
}

export function serializeActivationPacket(packet: ActivationPacket): SerializedActivationPacket {
  return {
    ...packet,
    encoded: {
      format: packet.encoded.format,
      mode: packet.encoded.mode,
      elementCount: packet.encoded.elementCount,
      blockSize: packet.encoded.blockSize,
      scalesBase64: bytesToBase64(float32ToBytes(packet.encoded.scales)),
      payloadBase64: bytesToBase64(packet.encoded.payload),
    },
  };
}

export function deserializeActivationPacket(packet: SerializedActivationPacket): ActivationPacket {
  if (packet.format !== NEXUS_ACTIVATION_PACKET_FORMAT) throw new Error("Unsupported activation packet format");
  const scales = bytesToFloat32(base64ToBytes(packet.encoded.scalesBase64));
  const payload = base64ToBytes(packet.encoded.payloadBase64);
  return {
    ...packet,
    encoded: {
      format: packet.encoded.format,
      mode: packet.encoded.mode,
      elementCount: packet.encoded.elementCount,
      blockSize: packet.encoded.blockSize,
      scales,
      payload,
    },
  };
}

export function measureActivationPackets(packets: readonly ActivationPacket[]): ActivationPacketMetrics {
  if (packets.length === 0) throw new RangeError("packets must not be empty");
  const originalBytes = packets.reduce((sum, packet) => sum + packet.originalBytes, 0);
  const encodedBytes = packets.reduce((sum, packet) => sum + packet.encodedBytes, 0);
  return {
    packetCount: packets.length,
    originalBytes,
    encodedBytes,
    compressionRatio: encodedBytes === 0 ? Number.POSITIVE_INFINITY : originalBytes / encodedBytes,
    spaceSavingFraction: originalBytes === 0 ? 0 : 1 - encodedBytes / originalBytes,
  };
}

export function packetizeActivationAdaptive(input: {
  streamId: string;
  tokenIndex: number;
  layerStart: number;
  layerEndExclusive: number;
  activation: Float32Array;
  previousActivation?: Float32Array;
  maxPayloadBytes?: number;
  maxAbsoluteError?: number;
  candidateBlockSizes?: readonly number[];
}): AdaptiveActivationPacketization {
  const maxAbsoluteError = input.maxAbsoluteError ?? 0.0025;
  if (!Number.isFinite(maxAbsoluteError) || maxAbsoluteError < 0) {
    throw new RangeError("maxAbsoluteError must be a finite non-negative number");
  }
  const candidates = input.candidateBlockSizes ?? [32, 64, 128, 256, 512, 1024];
  if (candidates.length === 0) throw new RangeError("candidateBlockSizes must not be empty");

  let bestWithinError: AdaptiveActivationPacketization | null = null;
  let bestFallback: AdaptiveActivationPacketization | null = null;
  for (const blockSize of candidates) {
    const packets = packetizeActivation({ ...input, blockSize });
    const decoded = reassembleActivationPackets(packets, input.previousActivation, {
      maxPacketPayloadBytes: input.maxPayloadBytes,
    });
    let observedMaxError = 0;
    for (let index = 0; index < input.activation.length; index += 1) {
      observedMaxError = Math.max(observedMaxError, Math.abs(decoded[index] - input.activation[index]));
    }
    const metrics = measureActivationPackets(packets);
    const candidate: AdaptiveActivationPacketization = {
      packets,
      metrics,
      selectedBlockSize: blockSize,
      maxAbsoluteError: observedMaxError,
      strategy: observedMaxError <= maxAbsoluteError ? "smallest-payload-within-error" : "fallback-lowest-error",
    };
    if (observedMaxError <= maxAbsoluteError) {
      if (!bestWithinError || metrics.encodedBytes < bestWithinError.metrics.encodedBytes) {
        bestWithinError = candidate;
      }
    }
    if (
      !bestFallback ||
      observedMaxError < bestFallback.maxAbsoluteError ||
      (observedMaxError === bestFallback.maxAbsoluteError && metrics.encodedBytes < bestFallback.metrics.encodedBytes)
    ) {
      bestFallback = candidate;
    }
  }
  return bestWithinError ?? bestFallback!;
}

export function reassembleActivationPackets(
  packets: readonly ActivationPacket[],
  previousActivation?: Float32Array,
  limits: LayerStreamLimits = {},
): Float32Array {
  if (packets.length === 0) throw new RangeError("packets must not be empty");
  const ordered = [...packets].sort((left, right) => left.sequence - right.sequence);
  const first = ordered[0];
  for (let index = 0; index < ordered.length; index += 1) {
    const packet = ordered[index];
    if (
      packet.format !== NEXUS_ACTIVATION_PACKET_FORMAT ||
      packet.streamId !== first.streamId ||
      packet.tokenIndex !== first.tokenIndex ||
      packet.layerStart !== first.layerStart ||
      packet.layerEndExclusive !== first.layerEndExclusive ||
      packet.sequence !== index ||
      packet.totalSequences !== ordered.length
    ) {
      throw new Error("Activation packet sequence is inconsistent");
    }
  }
  const maxActivationElements = limitValue(limits, "maxActivationElements");
  const maxPacketPayloadBytes = limitValue(limits, "maxPacketPayloadBytes");
  const decoded: Float32Array[] = [];
  let previousCursor = 0;
  let totalElements = 0;
  for (const packet of ordered) {
    if (packet.encoded.payload.byteLength > maxPacketPayloadBytes) {
      throw new RangeError("Activation packet exceeds the payload safety limit");
    }
    const previous = previousActivation?.slice(previousCursor, previousCursor + packet.encoded.elementCount);
    const chunk = decodeActivationDelta(packet.encoded, previous, {
      maxElementCount: maxActivationElements - totalElements,
      maxPayloadBytes: maxPacketPayloadBytes,
    });
    previousCursor += chunk.length;
    totalElements += chunk.length;
    if (totalElements > maxActivationElements) throw new RangeError("Reassembled activation exceeds the safety limit");
    decoded.push(chunk);
  }
  const output = new Float32Array(totalElements);
  let cursor = 0;
  for (const chunk of decoded) {
    output.set(chunk, cursor);
    cursor += chunk.length;
  }
  return output;
}

export function packetizeLayerBytes(input: {
  streamId: string;
  modelId: string;
  chunkId: string;
  bytes: Uint8Array;
  maxPacketBytes?: number;
}): readonly LayerBytePacket[] {
  assertSafeId(input.streamId, "streamId");
  assertSafeId(input.modelId, "modelId");
  if (input.chunkId.length < 1 || input.chunkId.length > 256) throw new Error("chunkId must be 1-256 characters");
  const maxPacketBytes = input.maxPacketBytes ?? DEFAULT_LIMITS.maxPacketPayloadBytes;
  if (!Number.isSafeInteger(maxPacketBytes) || maxPacketBytes <= 0) {
    throw new RangeError("maxPacketBytes must be a positive safe integer");
  }
  const totalSequences = Math.max(1, Math.ceil(input.bytes.byteLength / maxPacketBytes));
  const packets: LayerBytePacket[] = [];
  for (let sequence = 0; sequence < totalSequences; sequence += 1) {
    const start = sequence * maxPacketBytes;
    const end = Math.min(start + maxPacketBytes, input.bytes.byteLength);
    packets.push({
      format: NEXUS_LAYER_BYTE_PACKET_FORMAT,
      streamId: input.streamId,
      modelId: input.modelId,
      chunkId: input.chunkId,
      sequence,
      totalSequences,
      byteOffset: start,
      totalBytes: input.bytes.byteLength,
      dataBase64: bytesToBase64(input.bytes.slice(start, end)),
    });
  }
  return packets;
}

export function reassembleLayerBytePackets(
  packets: readonly LayerBytePacket[],
  limits: LayerStreamLimits = {},
): Uint8Array {
  if (packets.length === 0) throw new RangeError("packets must not be empty");
  const ordered = [...packets].sort((left, right) => left.sequence - right.sequence);
  const first = ordered[0];
  const maxPayloadBytes = limitValue(limits, "maxPacketPayloadBytes");
  if (first.totalBytes > limitValue(limits, "maxActivationElements") * Float32Array.BYTES_PER_ELEMENT) {
    throw new RangeError("Layer byte stream exceeds the safety limit");
  }
  const output = new Uint8Array(first.totalBytes);
  for (let index = 0; index < ordered.length; index += 1) {
    const packet = ordered[index];
    if (
      packet.format !== NEXUS_LAYER_BYTE_PACKET_FORMAT ||
      packet.streamId !== first.streamId ||
      packet.modelId !== first.modelId ||
      packet.chunkId !== first.chunkId ||
      packet.sequence !== index ||
      packet.totalSequences !== ordered.length ||
      packet.totalBytes !== first.totalBytes
    ) {
      throw new Error("Layer byte packet sequence is inconsistent");
    }
    const bytes = base64ToBytes(packet.dataBase64);
    if (bytes.byteLength > maxPayloadBytes) throw new RangeError("Layer byte packet exceeds the payload safety limit");
    if (packet.byteOffset + bytes.byteLength > output.byteLength) {
      throw new Error("Layer byte packet writes past the expected output");
    }
    output.set(bytes, packet.byteOffset);
  }
  return output;
}
